import { useCallback, useMemo, useState } from 'react';
import type { Activity, ActivityType, VibrationMode } from '@/types/activity';
import type { DownloadProgress } from '@/types/download';
import { useToast } from '@/contexts/toastContextBase';
import { downloadBlob } from '@/utils/download';
import { useFilteredActivitySelection } from '@/hooks/useFilteredActivitySelection';
import { isActivitySelectable } from '@/types/activity';
import {
  formatPatientDownloadSuccessMessage,
  getPatientDownloadModeConfig,
  getSelectedActivityIds,
  type DownloadMode,
} from './downloadWorkflow';

interface UsePatientDownloadControllerOptions {
  mode: DownloadMode;
  activities: Activity[];
}

export function usePatientDownloadController({
  mode,
  activities,
}: UsePatientDownloadControllerOptions) {
  const { showToast } = useToast();
  const config = useMemo(() => getPatientDownloadModeConfig(mode), [mode]);

  const [activityTypes, setActivityTypes] = useState<ActivityType[]>([]);
  const [vibrationModes, setVibrationModes] = useState<VibrationMode[]>([]);
  const [dateRange, setDateRange] = useState<{ from: string | null; to: string | null }>({
    from: null,
    to: null,
  });
  const [downloadProgress, setDownloadProgress] = useState<DownloadProgress>({
    status: 'idle',
    current: 0,
    total: 0,
  });

  const {
    filteredActivities,
    selectedActivities,
    selectedActivityIds,
    toggleActivitySelection,
    selectAllActivities,
    deselectAllActivities,
    resetSelection,
  } = useFilteredActivitySelection({
    activities,
    filters: {
      types: activityTypes,
      vibrationModes,
      startDate: dateRange.from ?? undefined,
      endDate: dateRange.to ?? undefined,
    },
    isSelectable: (activity) => mode !== 'processed' || isActivitySelectable(activity),
  });

  const hasFilters =
    activityTypes.length > 0 ||
    vibrationModes.length > 0 ||
    dateRange.from !== null ||
    dateRange.to !== null;

  const handleDateRangeChange = useCallback((from: string | null, to: string | null) => {
    setDateRange({ from, to });
  }, []);

  const clearFilters = useCallback(() => {
    setActivityTypes([]);
    setVibrationModes([]);
    setDateRange({ from: null, to: null });
    resetSelection();
  }, [resetSelection]);

  const handleDownload = useCallback(async () => {
    if (selectedActivities.length === 0) {
      showToast('warning', 'No activities selected');
      return;
    }

    setDownloadProgress({ status: 'fetching', current: 0, total: 1 });

    try {
      const activityIds = getSelectedActivityIds(selectedActivities);
      const zipBlob = await config.downloadAction(activityIds);

      setDownloadProgress({ status: 'packaging', current: 1, total: 1 });
      downloadBlob(zipBlob, `${config.filenamePrefix}-${new Date().toISOString().split('T')[0]}.zip`);

      setDownloadProgress({ status: 'complete', current: 1, total: 1 });
      showToast(
        'success',
        formatPatientDownloadSuccessMessage(selectedActivities.length, config.resultLabel)
      );

      setTimeout(() => {
        setDownloadProgress({ status: 'idle', current: 0, total: 0 });
      }, 2000);
    } catch (error) {
      setDownloadProgress({
        status: 'error',
        current: 0,
        total: 0,
        message: error instanceof Error ? error.message : 'Download failed',
      });
      showToast('error', config.downloadErrorMessage);
    }
  }, [selectedActivities, showToast, config]);

  return {
    config,
    activityTypes,
    vibrationModes,
    dateRange,
    filteredActivities,
    selectedActivities,
    selectedActivityIds,
    downloadProgress,
    hasFilters,
    setActivityTypes,
    setVibrationModes,
    handleDateRangeChange,
    clearFilters,
    toggleActivitySelection,
    selectAllActivities,
    deselectAllActivities,
    handleDownload,
  };
}
